import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SuccessNotification from '../../components/SuccessNotification';
import ErrorNotification from '../../components/ErrorNotification';

function InternalUserManagement() {
  const [startups, setStartups] = useState([]);
  const [selectedStartup, setSelectedStartup] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [successNotification, setSuccessNotification] = useState({ isVisible: false, message: "" });
  const [errorNotification, setErrorNotification] = useState({ isVisible: false, message: "" });
  
  // New state for existing users
  const [existingUsers, setExistingUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Member'
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/passwordless-login");
    } else {
      fetchStartups();
    }
  }, [navigate]);

  // Fetch existing users when startup selection changes
  useEffect(() => {
    fetchExistingUsers(selectedStartup);
  }, [selectedStartup]);

  const fetchStartups = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/fetchAllStartups", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStartups(response.data.startups || []);
    } catch (err) {
      console.error("Failed to fetch startups", err);
      setError(err.response?.data?.message || "Could not load startups.");
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingUsers = async (companyId) => {
    if (!companyId) {
      setExistingUsers([]);
      return;
    }

    setLoadingUsers(true);
    setUsersError(null);
    try {
      const token = localStorage.getItem("token");
      console.log(`Fetching users for company ID: ${companyId}`);
      
      const response = await axios.get("/api/admin/fetchUsersForCompany", {
        params: { companyId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('API Response:', response.data);
      
      const users = response.data.users || [];
      console.log(`Found ${users.length} users for this company`);
      
      setExistingUsers(users);
    } catch (err) {
      console.error("Failed to fetch existing users", err);
      console.error("Error details:", err.response?.data);
      setUsersError(err.response?.data?.message || "Could not load existing users.");
      setExistingUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStartup) {
      setErrorNotification({
        isVisible: true,
        message: "Please select a startup"
      });
      return;
    }

    if (!formData.firstName.trim() || !formData.email.trim()) {
      setErrorNotification({
        isVisible: true,
        message: "Please fill in first name and email address"
      });
      return;
    }

    // Check if email already exists in this company
    const emailExists = existingUsers.some(user => 
      user.email.toLowerCase() === formData.email.trim().toLowerCase()
    );
    if (emailExists) {
      setErrorNotification({
        isVisible: true,
        message: "This email address is already registered for this startup"
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/admin/createUser", {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyId: selectedStartup,
        role: formData.role
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccessNotification({
        isVisible: true,
        message: response.data.message
      });

      // Refresh the existing users list
      await fetchExistingUsers(selectedStartup);

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Member'
      });
      // Don't reset selectedStartup so user can see the updated team list
    } catch (err) {
      console.error("Failed to create user", err);
      setErrorNotification({
        isVisible: true,
        message: err.response?.data?.message || "Failed to create user"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStartups = startups.filter(startup =>
    startup.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    startup.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedStartupData = startups.find(s => s.id === selectedStartup);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img
                src="https://www.jumpstart-uk.com/wp-content/uploads/2022/05/jumpstart-logo-black.svg"
                className="h-8"
                alt="Jumpstart Logo"
              />
              <h1 className="ml-4 text-xl font-semibold text-gray-900">
                Internal User Management
              </h1>
            </div>
  

          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-medium text-blue-900 mb-2">Internal Admin Tool</h2>
          <p className="text-blue-700 text-sm">
            This tool allows you to create user accounts for any startup in the system. 
            Users will receive invitation emails and can log in using the passwordless authentication system.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Startup Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Startup</h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5 text-[#02B491]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  <span className="text-gray-500">Loading startups...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search startups by name or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                  />
                </div>

                {/* Startup Selection */}
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                  {filteredStartups.map((startup) => (
                                         <div
                       key={startup.id}
                       className={`p-3 cursor-pointer hover:bg-gray-50 ${
                         selectedStartup === startup.id ? 'bg-[#02B491] text-white' : ''
                       }`}
                       onClick={() => {
                         setSelectedStartup(startup.id);
                         // Clear any previous form validation errors
                         setErrorNotification({ isVisible: false, message: "" });
                       }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{startup.name}</h4>
                          <div className="text-sm opacity-75 space-y-1">
                            {startup.location && <div>📍 {startup.location}</div>}
                            {startup.fundingStage && <div>💰 {startup.fundingStage}</div>}
                            {startup.teamSize && <div>👥 {startup.teamSize} people</div>}
                          </div>
                        </div>
                        {selectedStartup === startup.id && (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {filteredStartups.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? 'No startups match your search' : 'No startups available'}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Selected Startup Info */}
          {selectedStartupData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900">Selected Startup:</h4>
              <p className="text-green-700">{selectedStartupData.name}</p>
              {selectedStartupData.location && (
                <p className="text-green-600 text-sm">Location: {selectedStartupData.location}</p>
              )}
            </div>
          )}
                 

          {/* Existing Team Members */}
          {selectedStartup && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Existing Team Members</h3>
                {existingUsers.length > 0 && (
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                    {existingUsers.length} member{existingUsers.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-[#02B491]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span className="text-gray-500">Loading team members...</span>
                  </div>
                </div>
              ) : usersError ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error loading team members</h3>
                      <p className="text-sm text-red-700 mt-1">{usersError}</p>
                    </div>
                  </div>
                </div>
              ) : existingUsers.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No team members yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This startup doesn't have any team members. Add the first one below!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {existingUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-[#02B491] rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {user.firstName?.[0] || user.email?.[0] || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                          user.role === 'Admin' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                          user.status === 'Active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {user.status}
                        </span>
                        {user.lastLogin && (
                          <span className="text-xs text-gray-400">
                            Last: {new Date(user.lastLogin).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* User Details Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                  placeholder="Doe (optional)"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                  placeholder="john.doe@company.com"
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                >
                  <option value="Member">Member</option>
                  <option value="Admin">Admin</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Admins can invite other team members. Members can only view and manage their company data.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || !selectedStartup || loading}
              className={`px-6 py-3 bg-[#02B491] text-white rounded-lg font-medium hover:bg-[#029c7a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors`}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Creating User...
                </>
              ) : (
                'Create User & Send Invitation'
              )}
            </button>
          </div>
        </form>


      </div>

      {/* Notifications */}
      <SuccessNotification
        isVisible={successNotification.isVisible}
        message={successNotification.message}
        onClose={() => setSuccessNotification({ isVisible: false, message: "" })}
      />
      <ErrorNotification
        isVisible={errorNotification.isVisible}
        message={errorNotification.message}
        onClose={() => setErrorNotification({ isVisible: false, message: "" })}
      />
    </div>
  );
}

export default InternalUserManagement; 